import React, { useContext, useState, useMemo, useEffect } from "react";
import { Container, Row, Col, Button, Form, Spinner } from "react-bootstrap";
import "../../../assests/css/teachers/TeacherStudentAttendance.css";
import TeacherMasterLayout from "../../../layouts/TeacherMasterLayout";
import { AppContext } from "../../../context/Appcontext";
import axios from "axios";

// ─────────────────────────────────────────────
//  API ENDPOINTS (Laravel backend se match karein)
// ─────────────────────────────────────────────
// POST ${baseUrl}/teacher/course/viewAllTeacherCourse   → logged-in teacher ke courses (TeacherCourses.jsx wala hi endpoint)
// GET  /api/teacher/courses/:courseId/students          → us course ke enrolled students
// POST /api/attendance                                   → attendance save karna
// GET  /api/attendance?course_id=&date=                  → (optional) existing attendance load karna

const TODAY_DATE = new Date().toLocaleDateString("en-PK", {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
});
const TODAY_ISO = new Date().toISOString().split("T")[0]; // YYYY-MM-DD, backend ko bhejne ke liye

// ─────────────────────────────────────────────
//  SUB-COMPONENTS
// ─────────────────────────────────────────────

const CourseCard = ({ course, onSelect }) => {
  // TeacherCourses.jsx jaisa hi fallback chain: ngcourse.name -> name -> title
  const courseName =
    course.ngcourse?.name || course.name || course.title || "—";

  return (
    <div className="ta-course-card" onClick={() => onSelect(course)}>
      <div className="ta-course-card-top">
        <span className="ta-course-code">{course.code || "N/A"}</span>
        <span className="ta-course-section-chip">
          {course.status || "Active"}
        </span>
      </div>
      <h3 className="ta-course-name">{courseName}</h3>
      <div className="ta-course-meta">
        <span>👥 {course.students || 0} Students</span>
      </div>
      <Button className="ta-btn-primary ta-course-btn">
        ▶ Take Attendance
      </Button>
    </div>
  );
};

const StudentRow = ({ student, index, onMark }) => (
  <div
    className={`ta-student-row ${
      student.attendance === "P"
        ? "ta-row-present"
        : student.attendance === "A"
          ? "ta-row-absent"
          : ""
    }`}
  >
    <div className="ta-col-serial">{index + 1}</div>

    <div className="ta-col-student">
      <div
        className="ta-student-avatar"
        style={{ background: student.avatarColor || "#6A88CE" }}
      >
        {student.initials}
      </div>
      <div>
        <div className="ta-student-name">{student.name}</div>
        <div className="ta-student-id">{student.id}</div>
      </div>
    </div>

    <div className="ta-col-roll">{student.rollNo}</div>

    <div className="ta-col-actions">
      <button
        className={`ta-mark-btn ta-mark-present ${student.attendance === "P" ? "active" : ""}`}
        onClick={() => onMark(student.id, "P")}
      >
        ✓ P
      </button>
      <button
        className={`ta-mark-btn ta-mark-absent ${student.attendance === "A" ? "active" : ""}`}
        onClick={() => onMark(student.id, "A")}
      >
        ✗ A
      </button>
      <button
        className={`ta-mark-btn ta-mark-leave ${student.attendance === "L" ? "active" : ""}`}
        onClick={() => onMark(student.id, "L")}
      >
        L
      </button>
    </div>
  </div>
);

const Toast = ({ message, type, onClose }) => {
  React.useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`ta-toast ta-toast-${type}`}>
      {type === "success" ? "✅" : "❌"} {message}
    </div>
  );
};

// ─────────────────────────────────────────────
//  MAIN COMPONENT
// ─────────────────────────────────────────────

const TeacherStudentAttendance = () => {
  const { baseUrl, loggedInTeacher, setSelectedCourseForAttendance } =
    useContext(AppContext);

  const [courses, setCourses] = useState([]);
  const [activeCourse, setActiveCourse] = useState(null);
  const [students, setStudents] = useState([]);
  const [isMark, setIsMark] = useState("false");
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState(null);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [saving, setSaving] = useState(false);

  // Teacher ke courses load karna — TeacherCourses.jsx wala hi endpoint & pattern
  const viewAllTeacherCourse = () => {
    setLoadingCourses(true);
    const formData = new FormData();
    formData.append("ngteacher_id", loggedInTeacher?.id);

    axios
      .post(`${baseUrl}/teacher/course/viewAllTeacherCourse`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.success == true) {
          setCourses(response.data.courses);
        }
        setLoadingCourses(false);
      })
      .catch((error) => {
        console.log(error);
        showToast("Courses load nahi ho sake", "error");
        setLoadingCourses(false);
      });
  };

  useEffect(() => {
    viewAllTeacherCourse();
  }, []);

  // Course select hone par: (1) context mein save karna (2) uske students load karna
  const handleSelectCourse = (course) => {
    setActiveCourse(course);
    setSelectedCourseForAttendance(course); // ← context getter yahi id aage use karega
    setSearchQuery("");
    fetchStudentsForCourse(course.id);
  };

  const fetchStudentsForCourse = (courseId) => {
    setLoadingStudents(true);
    const formData = new FormData();
    formData.append("course_id", courseId);
    formData.append("date", TODAY_ISO);

    axios
      .post(`${baseUrl}/teacher/attendance/getStudentsForCourse`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      // .then((response) => {
      //   if (response.data.success == true) {
      //     setStudents(response.data.students || []);   // ✅ fallback empty array
      //   } else {
      //     setStudents([]);
      //   }
      //   setLoadingStudents(false);
      // })
      .then((response) => {
        if (response.data.success == true) {
          // Students set karo
          setStudents(response.data.students || []);

          // Backend se isMark lo
          setIsMark(response.data.isMark);
        } else {
          setStudents([]);

          // Agar error ho to false
          setIsMark("false");
        }

        setLoadingStudents(false);
      })
      .catch((error) => {
        console.log(error);
        showToast("Students load nahi ho sake", "error");
        setStudents([]);
            setIsMark("false");
        setLoadingStudents(false);
      });
  };

  const handleBackToCourses = () => {
    setActiveCourse(null);
    setSelectedCourseForAttendance(null);
    setStudents([]);
        setIsMark("false");
  };

  // const handleMark = (studentId, status) => {
  //   setStudents((prev) =>
  //     prev.map((s) => (s.id === studentId ? { ...s, attendance: status } : s)),
  //   );
  // };
 const handleMark = (studentId, status) => {
  setStudents((prev) =>
    prev.map((s) => {

      // Agar attendance already marked hai
      // to change nahi karni
      if (s.id === studentId && s.attendance !== null) {
        return s;
      }

      // Agar attendance abhi null hai
      // to pehli dafa mark karo
      if (s.id === studentId && s.attendance === null) {
        return {
          ...s,
          attendance: status,
        };
      }

      return s;
    })
  );
};

  // const handleMarkAll = (status) => {
  //   setStudents((prev) => prev.map((s) => ({ ...s, attendance: status })));
  //   showToast(
  //     status === "P"
  //       ? "All students marked present"
  //       : "All students marked absent",
  //     "success",
  //   );
  // };
 const handleMarkAll = (status) => {
  setStudents((prev) =>
    prev.map((s) => {

      // Already marked hai to change nahi karna
      if (s.attendance !== null) {
        return s;
      }

      // Sirf unmarked students ko mark karo
      return {
        ...s,
        attendance: status,
      };
    })
  );

  setIsMark("false");

  showToast(
    status === "P"
      ? "Unmarked students marked present"
      : "Unmarked students marked absent",
    "success",
  );
};
  const handleSave = () => {
    const unmarked = students.filter((s) => s.attendance === null).length;
    if (unmarked > 0) {
      showToast(`${unmarked} students still unmarked`, "error");
      return;
    }
    setSaving(true);
    const formData = new FormData();
    formData.append("course_id", activeCourse.id);
    formData.append("date", TODAY_ISO);
    formData.append(
      "records",
      JSON.stringify(
        students.map((s) => ({ student_id: s.id, status: s.attendance })), // 'P' | 'A' | 'L'
      ),
    );

    axios
      .post(`${baseUrl}/teacher/attendance/save`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.data.success == true) {
          setIsMark(response.data.isMark);
          showToast("Attendance saved successfully", "success");
        } else {
          showToast("Attendance save nahi ho saki", "error");
        }
        setSaving(false);
      })
    .catch((error) => {
  console.log("SAVE ATTENDANCE ERROR:", error);
  console.log("BACKEND RESPONSE:", error.response?.data);

  showToast(
    error.response?.data?.error ||
      error.response?.data?.message ||
      "Attendance save nahi ho saki",
    "error"
  );

  setSaving(false);
});
  };

  const showToast = (message, type) => setToast({ message, type });

  const filteredStudents = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return students;
    return students.filter(
      (s) =>
        s.name.toLowerCase().includes(q) || s.rollNo.toLowerCase().includes(q),
    );
  }, [students, searchQuery]);

  const markedCount = students.filter((s) => s.attendance !== null).length;
  const presentCount = students.filter((s) => s.attendance === "P").length;
  const absentCount = students.filter((s) => s.attendance === "A").length;
  const leaveCount = students.filter((s) => s.attendance === "L").length;
  const progressPct = students.length
    ? Math.round((markedCount / students.length) * 100)
    : 0;

  return (
    <TeacherMasterLayout>
      <div className="ta-page">
        <Container fluid className="ta-main">
          <div className="ta-page-header">
            <h1 className="ta-page-title">
              {activeCourse
                ? activeCourse.ngcourse?.name ||
                  activeCourse.name ||
                  activeCourse.title ||
                  "—"
                : "My Courses"}
            </h1>
            <p className="ta-page-subtitle">
              <span className="ta-live-dot" /> {TODAY_DATE}
            </p>
          </div>

          {/* ── COURSES GRID ── */}
          {!activeCourse &&
            (loadingCourses ? (
              <div className="ta-loading-state text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : courses.length === 0 ? (
              <div className="ta-empty-state">
                <div className="ta-empty-icon">📚</div>
                <h5>No courses assigned</h5>
              </div>
            ) : (
              <Row className="g-4">
                {courses.map((course) => (
                  <Col key={course.id} xs={12} sm={6} lg={4} xl={3}>
                    <CourseCard course={course} onSelect={handleSelectCourse} />
                  </Col>
                ))}
              </Row>
            ))}

          {/* ── STUDENT LIST ── */}
          {activeCourse && (
            <Row>
              <Col xs={12}>
                <div className="ta-panel">
                  <div className="ta-panel-header">
                    <div>
                      <div className="ta-panel-title">
                        {activeCourse.ngcourse?.name ||
                          activeCourse.name ||
                          activeCourse.title ||
                          "—"}
                      </div>
                      {/* <div className="ta-panel-meta">
                        <span className="ta-chip">
                          {activeCourse.code || "N/A"}
                        </span>
                        <span className="ta-chip">
                          {activeCourse.status || "Active"}
                        </span>
                        <span className="ta-chip">
                          👥 {activeCourse.students || 0} Students
                        </span>
                      </div> */}
                      <div className="ta-panel-meta">
                        <span className="ta-chip">
                          {activeCourse.code || "N/A"}
                        </span>

                        <span className="ta-chip">
                          {activeCourse.status || "Active"}
                        </span>

                        <span className="ta-chip">
                          👥 {students.length} Students
                        </span>

                        <span className="ta-chip">
                          {isMark === "true"
                            ? "✓ Attendance Marked"
                            : "○ Attendance Pending"}
                        </span>
                      </div>
                    </div>
                    <div className="ta-panel-actions">
                      <Button
                        className="ta-btn-white"
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "💾 Save"}
                      </Button>
                      <Button
                        className="ta-btn-outline-white"
                        onClick={handleBackToCourses}
                      >
                        ← Back
                      </Button>
                    </div>
                  </div>

                  {loadingStudents ? (
                    <div className="ta-loading-state text-center py-5">
                      <Spinner animation="border" />
                    </div>
                  ) : (
                    <>
                      <div className="ta-toolbar">
                        <Row className="g-3 align-items-center">
                          <Col xs={12} md={5}>
                            <Form.Control
                              type="text"
                              placeholder="Search by name or roll no..."
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              className="ta-search-input"
                            />
                          </Col>
                          <Col xs={12} md={4} className="ta-bulk-actions">
                            <button
                              className="ta-bulk-btn ta-bulk-present"
                              onClick={() => handleMarkAll("P")}
                            >
                              ✓ All Present
                            </button>
                            <button
                              className="ta-bulk-btn ta-bulk-absent"
                              onClick={() => handleMarkAll("A")}
                            >
                              ✗ All Absent
                            </button>
                          </Col>
                          <Col xs={12} md={3}>
                            <div className="ta-progress-wrap">
                              <div className="ta-progress-track">
                                <div
                                  className="ta-progress-fill"
                                  style={{ width: `${progressPct}%` }}
                                />
                              </div>
                              <span className="ta-progress-count">
                                {markedCount}/{students.length}
                              </span>
                            </div>
                          </Col>
                        </Row>
                      </div>

                      <div className="ta-summary-bar">
                        <span className="ta-summary-present">
                          ✓ Present: {presentCount}
                        </span>
                        <span className="ta-summary-absent">
                          ✗ Absent: {absentCount}
                        </span>
                        <span className="ta-summary-leave">
                          L Leave: {leaveCount}
                        </span>
                        <span className="ta-summary-unmarked">
                          Unmarked: {students.length - markedCount}
                        </span>
                      </div>

                      <div className="ta-list-header">
                        <div>#</div>
                        <div>Student</div>
                        <div>Roll No</div>
                        <div>Attendance</div>
                      </div>

                      <div className="ta-list-body">
                        {filteredStudents.length === 0 ? (
                          <div className="ta-empty-state">
                            <div className="ta-empty-icon">🔍</div>
                            <h5>No students found</h5>
                            <p>Try a different name or roll number</p>
                          </div>
                        ) : (
                          filteredStudents.map((student, idx) => (
                            <StudentRow
                              key={student.id}
                              student={student}
                              index={idx}
                              onMark={handleMark}
                            />
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </Container>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </TeacherMasterLayout>
  );
};

export default TeacherStudentAttendance;
